<template>
    <div class="position-relative sku-input-container" :style="{width: `${popupWidth}px`}">
        <input type="text"
               :ref="id"
               :attr-ref="id"
               :value="value"
               @input="e => onInput(e.target.value)"
               @blur="onBlur"
               @focus="onFocus"
               @keyup.esc.stop="onEscape"
               @keydown.enter.stop.prevent="onEnter"
               @keydown.up.stop.prevent="onKeyUp"
               @keydown.down.stop.prevent="onKeyDown"
               placeholder="Stock code"
               class="form-control form-control-sm">
        <span v-if="isSearching" class="search-sku-spinner">
            <i class="fas fa-spinner fa-pulse"></i>
        </span>

        <!-- DROPDOWN -->
        <div class="app-dropdown">
            <template v-if="hits.length">
                <div v-for="(item, i) in hits" :key="i"
                     @mousedown="onSelectItem(item)"
                     @mouseover="onMouseOverItem(i)"
                     @click.stop="onClickItem"
                     class="app-dropdown-item"
                     :class="{
                         'app-dropdown-item-active': i === hi,
                         'app-dropdown-item-server-sku': !!item.uuid
                     }">
                    <div class="media">
                        <span v-show="item.uuid">
                            <i class="fal fa-building mr-1"></i>
                        </span>
                        <div class="media-body">
                            {{ item.sku }}
                            <div v-if="item.description && showDescription" class="text-muted font-12">
                                {{ item.description }}
                            </div>
                        </div>
                    </div>
                </div>
            </template>
            <div v-else-if="!hits.length && !isSearching" class="app-dropdown-no-results">
                No result found
            </div>
        </div>
    </div>
</template>

<script>
import Vue from 'vue'
import axios from 'axios'
import {ERROR} from '@/store/modules/ui/UI'
import {
    FieldEditorOptionsSources,
    FieldStorageOptionsGetters,
    getFieldStorageOptionsLabel,
    getFieldStorageOptionsTrackBy,
    getFormFieldOptions
} from '@/types/document-viewer/field/modules/editor/select'
import {getFieldDependencyQueryParams} from '@/types/document-viewer/field/modules/dependencies'

export default Vue.extend({
    name: 'AgGridCellEditorStockCode',
    data() {
        return {
            value: null,
            isSearching: false,
            hits: [],
            hi: -1 // highlighted hits index (can be entered)
        }
    },
    computed: {
        lineIdx() {
            return this.params.data ? this.params.data.lineIdx : -1
        },

        id() {
            return `sku-${this.lineIdx}`
        },

        popupWidth() {
            return Math.max(this.params.column.actualWidth, 200)
        },

        showDescription() {
            return _.get(this.params, 'settings.editor.select.showDescription')
        },

        dependencies() {
            return getFieldDependencyQueryParams({
                field: this.params.settings,
                docData: this.getResourceState(),
                lineIdx: this.lineIdx
            })
        }
    },
    methods: {
        isPopup() {
            return true
        },

        getValue() {
            return this.value
        },

        onInput(value) {
            this.value = value
            this.getHits(value)
        },

        onEnter() {
            if (!this.hits.length || this.hi === -1) {
                this.stopEditing()
                return
            }
            this.value = this.hits[this.hi]['sku']
            this.stopEditing()
        },

        onSelectItem(item) {
            this.value = item['sku']
            this.stopEditing()
        },

        onBlur() {
        },

        onFocus() {
            if (!this.value) {
                this.getHits()
            }
        },

        onEscape() {
            this.stopEditing()
        },

        onKeyUp() {
            if (this.hi === 0 || this.hi === -1) {
                this.hi = this.hits.length - 1
            } else {
                this.hi--
            }
        },

        onKeyDown() {
            if (this.hi === this.hits.length - 1) {
                this.hi = 0
            } else {
                this.hi++
            }
        },

        // DROPDOWN INPUT LISTENERS
        onClickItem() {
            this.$refs[this.id].focus()
        },

        onMouseOverItem(idx) {
            this.hi = idx
        },

        async stopEditing() {
            await this.$nextTick()

            // NOTE: force value change even if there are no changes
            // (in order to run check and autofill stock item data)
            const {column, rowIndex} = this.params.api.getFocusedCell()
            this.params.node.setDataValue(column.getColId(), this.value)
            this.params.api.setFocusedCell(rowIndex, column.getColId())
            this.params.api.stopEditing()
        },

        async getHits(query = '') {
            let data = []
            try {
                this.isSearching = true
                const res = await getFormFieldOptions({
                    // For old forms this data is not set.
                    // To use a common request for new and old forms -
                    // we inject hardcoded select model that works for both
                    select: {
                        source: FieldEditorOptionsSources.STORAGE,
                        trackBy: getFieldStorageOptionsTrackBy(FieldStorageOptionsGetters.ITEMS),
                        label: getFieldStorageOptionsLabel(FieldStorageOptionsGetters.ITEMS),
                        storage: {
                            getter: FieldStorageOptionsGetters.ITEMS
                        },
                        filters: _.get(this.params, 'settings.editor.select.filters')
                    },
                    params: {
                        q: query,
                        page: 1,
                        invoiceUUID: this.params.documentUuid,
                        dependencies: this.dependencies
                    },
                    requestId: 'SEARCH_ITEMS',
                    filtered: true
                })
                data = res.data
                this.isSearching = false
            } catch (e) {
                if (!axios.isCancel(e)) {
                    this.$snackbar.show({type: ERROR, text: 'Search error'})
                    this.hi = -1
                    this.isSearching = false
                }
            }
            this.hits = data
        },

        viewItem(uuid) {
            this.$modal.show('edit-item', {uuid})
        },

        getResourceState() {
            if (typeof this.params.getResourceState !== 'function') {
                return null
            }
            return this.params.getResourceState()
        }
    },
    mounted() {
        this.value = this.params.value
        this.getHits(this.value)
        Vue.nextTick(() => {
            this.$refs[this.id].focus()
        })
    }
})
</script>

<style scoped lang="scss">
label {
    color: #2c3e50;
    margin-bottom: 2px;
}

.sku-input-container {
    height: 260px;
    width: 200px;
    position: relative;
}

.app-dropdown {
    height: 230px;
    overflow-y: auto;
    width: 100%;
}

.search-sku-spinner {
    position: absolute;
    right: 8px;
    top: 8px;
}

.is-not-matched-item {
    position: absolute;
    top: 10px;
    right: -22px;
    color: var(--warning);

    &:hover {
        cursor: pointer;
    }

    .count {
        position: absolute;
        top: -10px;
        right: -7px;
        font-size: 12px;
        font-weight: 600;
    }
}

.matched-item {
    position: absolute;
    top: 10px;
    right: -46px;
    color: var(--success);

    &:hover {
        cursor: pointer;
    }
}
</style>
